import React from 'react'
import { Icon } from '@chakra-ui/react'
import { gql, useMutation } from '@apollo/client'
import { FaArrowsRotate } from 'react-icons/fa6'

import SharedButton from '~components/shared/buttons/shared-button'
import { useClinic } from '~contexts/clinic-context'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'
import { PracticeManagementSystem, namedOperations } from '~graphql/generated/graphql'

const SYNC_PIMS_REMINDERS_FOR_PATIENT = gql`
    mutation syncPimsRemindersForPatient($patientId: ID!) {
        syncPimsRemindersForPatient(patientId: $patientId)
    }
`

/** PIMS that support on-demand patient reminder sync. */
const REAL_TIME_REMINDER_SYNC_PMSES: PracticeManagementSystem[] = [
    PracticeManagementSystem.Pulse,
    PracticeManagementSystem.Shepherd,
    PracticeManagementSystem.Daysmart,
    PracticeManagementSystem.Provet,
    PracticeManagementSystem.EzyVet,
]

interface Props {
    patient: {
        id: string
        pms_id?: string | null
    }
}

export default function SyncPatientRemindersButton({ patient }: Props) {
    const { clinic } = useClinic()
    const { onCompleted, onError } = useToastFeedback(
        'Reminders synced from PIMS.',
        'Failed to sync reminders from PIMS.',
    )

    const [syncReminders, { loading }] = useMutation(
        SYNC_PIMS_REMINDERS_FOR_PATIENT,
        {
            onCompleted,
            onError,
            refetchQueries: [namedOperations.Query.getPimsRemindersByPatient],
            awaitRefetchQueries: true,
        },
    )

    if (
        !clinic?.id ||
        !patient.pms_id ||
        !clinic.pms ||
        !REAL_TIME_REMINDER_SYNC_PMSES.includes(clinic.pms)
    ) {
        return null
    }

    return (
        <SharedButton
            size="sm"
            variant="outline"
            leftIcon={<Icon as={FaArrowsRotate} />}
            isLoading={loading}
            onClick={() =>
                syncReminders({
                    variables: {
                        patientId: patient.id,
                    },
                })
            }
        >
            Sync reminders
        </SharedButton>
    )
}
